import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Building2, MoreHorizontal, Eye, Pencil, Trash2, Wrench, Phone, PoundSterling
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ScoreBadge } from "@/components/ScoreBadge";

const stageConfig = {
  new: { label: "New", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  contacted: { label: "Contacted", cls: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" },
  qualified: { label: "Qualified", cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  proposal: { label: "Proposal", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  negotiation: { label: "Negotiation", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  won: { label: "Won", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  lost: { label: "Lost", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export const MobileLeadCard = ({ lead, onView, onEdit, onDelete }) => {
  const stage = stageConfig[lead.stage] || { label: lead.stage, cls: "" };
  return (
    <div
      data-testid={`lead-card-mobile-${lead.id}`}
      className="p-4 rounded-sm border border-white/5 bg-card hover:bg-white/[0.03] transition-colors duration-150"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-sm bg-blue-500/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{lead.company_name}</p>
            <p className="text-xs text-muted-foreground">{lead.contact_name}</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`lead-mobile-actions-${lead.id}`}>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-white/10">
            <DropdownMenuItem onClick={() => onView(lead)}><Eye className="w-4 h-4 mr-2" /> View</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(lead)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(lead.id)} className="text-red-400 focus:text-red-400">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Badge variant="outline" className={cn("text-[10px] rounded-sm", stage.cls)}>{stage.label}</Badge>
        <div className="flex items-center gap-1">
          <Wrench className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">{lead.service_type}</span>
        </div>
        {lead.score != null && <ScoreBadge score={lead.score} />}
        <div className="flex items-center gap-1 ml-auto">
          <PoundSterling className="w-3 h-3 text-amber-400" />
          <span className="text-xs font-semibold text-amber-400">{Number(lead.estimated_value).toLocaleString()}</span>
        </div>
      </div>
      {lead.phone && (
        <div className="flex items-center gap-1 mt-2">
          <Phone className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">{lead.phone}</span>
        </div>
      )}
    </div>
  );
};
